import { CalendarDays, MapPin, Ticket, Volume2, VolumeX } from "lucide-react";
import { useRef, useState, type MouseEvent } from "react";
import { Link } from "react-router-dom";
import type { EventItem } from "../../types/api";
import { getEventPrimaryActionLabel } from "../../utils/eventLabels";
import { formatCurrency, formatEventDate } from "../../utils/format";

export default function EventCard({ event, mode = "default" }: { event: EventItem; mode?: "default" | "past" }) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const hasTrailer = Boolean(event.trailerVideo);

  const startPreview = () => {
    if (!hasTrailer) return;
    setIsPreviewing(true);
    if (videoRef.current) {
      videoRef.current.muted = isMuted;
      videoRef.current.volume = isMuted ? 0 : 1;
    }
    void videoRef.current?.play().catch(() => {
      setIsPreviewing(false);
    });
  };

  const stopPreview = () => {
    if (!hasTrailer) return;
    setIsPreviewing(false);
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
  };

  const toggleMute = (clickEvent: MouseEvent<HTMLButtonElement>) => {
    clickEvent.preventDefault();
    clickEvent.stopPropagation();

    const nextMuted = !isMuted;
    setIsMuted(nextMuted);

    if (!videoRef.current) return;
    videoRef.current.muted = nextMuted;
    videoRef.current.volume = nextMuted ? 0 : 1;

    if (!isPreviewing) {
      setIsPreviewing(true);
      void videoRef.current.play().catch(() => {
        setIsPreviewing(false);
      });
    }
  };

  return (
    <article
      className="surface-card overflow-hidden"
      onMouseEnter={startPreview}
      onMouseLeave={stopPreview}
    >
      <div className="relative h-56 overflow-hidden">
        <img
          src={event.image}
          alt={event.title}
          className={`h-full w-full object-cover transition duration-500 ${
            isPreviewing ? "scale-105 opacity-0" : "opacity-100 hover:scale-105"
          }`}
        />
        {hasTrailer && (
          <video
            ref={videoRef}
            src={event.trailerVideo}
            muted={isMuted}
            loop
            playsInline
            preload="metadata"
            poster={event.image}
            className={`absolute inset-0 h-full w-full object-cover transition duration-500 ${
              isPreviewing ? "scale-105 opacity-100" : "opacity-0"
            }`}
            aria-label={`${event.title} trailer preview`}
          />
        )}
        {hasTrailer && (
          <button
            type="button"
            onClick={toggleMute}
            className="absolute bottom-4 right-4 z-10 flex h-11 w-11 items-center justify-center rounded-full bg-black/70 text-white shadow-lg backdrop-blur transition hover:bg-black dark:bg-[var(--pp-gold)] dark:text-[#0b1220] dark:hover:bg-[#e2a503] focus:outline-none focus:ring-2 focus:ring-[var(--pp-gold)]"
            aria-label={isMuted ? `Unmute ${event.title} trailer` : `Mute ${event.title} trailer`}
            title={isMuted ? "Unmute trailer" : "Mute trailer"}
          >
            {isMuted ? <VolumeX size={18} /> : <Volume2 size={18} />}
          </button>
        )}
        <div className="absolute left-4 top-4 rounded-full bg-black/85 px-3 py-1 font-display text-xs font-semibold uppercase tracking-[0.2em] text-aan-bright">
          {event.category}
        </div>
      </div>

      <div className="space-y-5 p-6">
        <div className="space-y-3">
          <Link
            to={`/screening/${event.id}`}
            className="block font-display text-2xl font-black uppercase leading-none text-foreground transition-colors hover:text-aan-bright"
          >
            {event.title}
          </Link>
          <p className="line-clamp-2 text-sm leading-6 text-muted-foreground">{event.description}</p>
        </div>

        <div className="space-y-3 text-sm text-muted-foreground">
          <div className="flex items-center gap-3">
            <CalendarDays size={16} className="text-[var(--pp-salmon)]" />
            <span>{formatEventDate(event.date, event.time)}</span>
          </div>
          <div className="flex items-center gap-3">
            <MapPin size={16} className="text-[var(--pp-teal)]" />
            <span>{event.venue}</span>
          </div>
        </div>

        <div className="flex items-center justify-between border-t border-line pt-4">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-slate-400">From</div>
            <div className="text-2xl font-bold text-foreground">{formatCurrency(event.stats.minPrice)}</div>
          </div>
          <Link to={`/screening/${event.id}`} className="pill-button bg-aan-green text-black hover:bg-aan-bright">
            <Ticket size={16} />
            {mode === "past" ? "View event" : getEventPrimaryActionLabel(event)}
          </Link>
        </div>
      </div>
    </article>
  );
}
